import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Switch } from '@/components/ui/switch';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { Webhook, Plus, Trash2, TestTube, RefreshCw, Check, X, Clock, Globe, Shield } from 'lucide-react';

interface ChatbotWebhook {
  id: string;
  tenant_id: string;
  webhook_url: string;
  event_type: string;
  is_active: boolean;
  secret_key: string | null;
  description: string | null;
  last_triggered_at: string | null;
  last_status_code: number | null;
  created_at: string;
}

interface TenantInfo {
  id: string;
  email: string;
  contact_name: string;
  company_name: string | null;
  plan: string;
}

const EVENT_TYPES = [
  { value: 'new_conversation', label: 'Neue Konversation', description: 'Wenn ein Chat gestartet wird' },
  { value: 'lead_captured', label: 'Lead erfasst', description: 'Wenn Kontaktdaten hinterlassen werden' },
  { value: 'callback_requested', label: 'Rückruf angefordert', description: 'Wenn ein Rückruf gewünscht wird' },
  { value: 'conversation_ended', label: 'Chat beendet', description: 'Wenn eine Konversation endet' },
];

const PLAN_WEBHOOK_LIMITS: Record<string, number> = {
  free: 0,
  basic: 1,
  pro: 5,
  premium: 999,
};

export function ChatbotWebhooks() {
  const [webhooks, setWebhooks] = useState<ChatbotWebhook[]>([]);
  const [tenants, setTenants] = useState<TenantInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState<string>('');
  const [newUrl, setNewUrl] = useState('');
  const [newEventType, setNewEventType] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newSecretKey, setNewSecretKey] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState<string | null>(null);
  const { toast } = useToast();

  const loadData = async () => {
    setLoading(true);
    try {
      const [{ data: webhooksData, error: whErr }, { data: tenantsData, error: tErr }] = await Promise.all([
        supabase.from('chatbot_webhooks').select('*').order('created_at', { ascending: false }),
        supabase.from('chatbot_tenants').select('id, email, contact_name, company_name, plan'),
      ]);
      if (whErr) throw whErr;
      if (tErr) throw tErr;
      setWebhooks(webhooksData || []);
      setTenants(tenantsData || []);
    } catch (error: any) {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const getTenantName = (tenantId: string) => {
    const t = tenants.find(t => t.id === tenantId);
    return t ? (t.company_name || t.contact_name) : tenantId.slice(0, 8);
  };

  const getTenantPlan = (tenantId: string) => {
    return tenants.find(t => t.id === tenantId)?.plan || 'free';
  };

  const getWebhookCountForTenant = (tenantId: string) => {
    return webhooks.filter(w => w.tenant_id === tenantId).length;
  };

  const canAddWebhook = (tenantId: string) => {
    const plan = getTenantPlan(tenantId);
    const limit = PLAN_WEBHOOK_LIMITS[plan] || 0;
    return getWebhookCountForTenant(tenantId) < limit;
  };

  const handleAdd = async () => {
    if (!selectedTenant || !newUrl || !newEventType) {
      toast({ title: 'Fehler', description: 'Bitte alle Pflichtfelder ausfüllen.', variant: 'destructive' });
      return;
    }

    if (!canAddWebhook(selectedTenant)) {
      const plan = getTenantPlan(selectedTenant);
      toast({ title: 'Limit erreicht', description: `Plan "${plan}" erlaubt max. ${PLAN_WEBHOOK_LIMITS[plan]} Webhook(s).`, variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const { error } = await supabase.from('chatbot_webhooks').insert({
        tenant_id: selectedTenant,
        webhook_url: newUrl,
        event_type: newEventType,
        description: newDescription || null,
        secret_key: newSecretKey || null,
      });
      if (error) throw error;
      toast({ title: 'Webhook erstellt', description: 'Der Webhook wurde erfolgreich hinzugefügt.' });
      setShowAddDialog(false);
      setNewUrl(''); setNewEventType(''); setNewDescription(''); setNewSecretKey(''); setSelectedTenant('');
      loadData();
    } catch (error: any) {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (id: string, isActive: boolean) => {
    try {
      const { error } = await supabase.from('chatbot_webhooks').update({ is_active: !isActive }).eq('id', id);
      if (error) throw error;
      setWebhooks(prev => prev.map(w => w.id === id ? { ...w, is_active: !isActive } : w));
    } catch (error: any) {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase.from('chatbot_webhooks').delete().eq('id', id);
      if (error) throw error;
      toast({ title: 'Gelöscht', description: 'Webhook wurde entfernt.' });
      setWebhooks(prev => prev.filter(w => w.id !== id));
    } catch (error: any) {
      toast({ title: 'Fehler', description: error.message, variant: 'destructive' });
    }
  };

  const handleTest = async (webhook: ChatbotWebhook) => {
    setTesting(webhook.id);
    try {
      const response = await fetch(
        `https://${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/functions/v1/chatbot-webhook-dispatch`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}` },
          body: JSON.stringify({
            tenantId: webhook.tenant_id,
            eventType: webhook.event_type,
            data: {
              test: true,
              message: 'Dies ist ein Test-Event von Klartech Chatbot.',
              timestamp: new Date().toISOString(),
            },
          }),
        }
      );
      const result = await response.json();
      if (response.ok) {
        toast({ title: 'Test gesendet', description: `${result.dispatched} Webhook(s) ausgelöst.` });
        loadData();
      } else {
        throw new Error(result.error || 'Test fehlgeschlagen');
      }
    } catch (error: any) {
      toast({ title: 'Testfehler', description: error.message, variant: 'destructive' });
    } finally {
      setTesting(null);
    }
  };

  const getStatusBadge = (code: number | null) => {
    if (code === null) return <Badge variant="outline" className="text-xs">Nie</Badge>;
    if (code >= 200 && code < 300) return <Badge className="bg-green-600 text-white text-xs"><Check className="h-3 w-3 mr-1" />{code}</Badge>;
    return <Badge variant="destructive" className="text-xs"><X className="h-3 w-3 mr-1" />{code}</Badge>;
  };

  const formatDate = (d: string | null) => {
    if (!d) return '—';
    return new Date(d).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="space-y-6">
      {/* Info Banner */}
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="pt-6">
          <div className="flex items-start gap-4">
            <Globe className="h-8 w-8 text-primary shrink-0" />
            <div>
              <h3 className="font-semibold mb-1">Webhook-Integrationen</h3>
              <p className="text-sm text-muted-foreground">
                Verbinden Sie den Chatbot mit externen Systemen wie Zapier, Make.com, n8n oder Ihren eigenen APIs.
                Events werden automatisch an die konfigurierten URLs gesendet.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Webhooks Table */}
      <Card>
        <CardHeader className="border-b">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl flex items-center gap-2">
                <Webhook className="w-5 h-5" />
                Webhooks ({webhooks.length})
              </CardTitle>
              <CardDescription>Konfigurierte Webhook-Endpunkte</CardDescription>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadData} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
              <Button size="sm" onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Webhook hinzufügen
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12 text-muted-foreground">
              <RefreshCw className="h-8 w-8 mx-auto mb-4 animate-spin" />
              <p>Lade Webhooks...</p>
            </div>
          ) : webhooks.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Webhook className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">Keine Webhooks konfiguriert</p>
              <p className="text-sm">Fügen Sie einen Webhook hinzu, um Events an externe Systeme zu senden.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/50">
                    <TableHead>Kunde</TableHead>
                    <TableHead>Event</TableHead>
                    <TableHead>URL</TableHead>
                    <TableHead>Signatur</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Letzter Aufruf</TableHead>
                    <TableHead>Aktiv</TableHead>
                    <TableHead>Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell className="font-medium text-sm">{getTenantName(webhook.tenant_id)}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {EVENT_TYPES.find(e => e.value === webhook.event_type)?.label || webhook.event_type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="max-w-xs truncate text-xs font-mono text-muted-foreground" title={webhook.webhook_url}>
                          {webhook.webhook_url}
                        </div>
                        {webhook.description && (
                          <div className="text-xs text-muted-foreground mt-1">{webhook.description}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        {webhook.secret_key ? (
                          <Badge variant="outline" className="text-xs"><Shield className="h-3 w-3 mr-1" />HMAC</Badge>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </TableCell>
                      <TableCell>{getStatusBadge(webhook.last_status_code)}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {formatDate(webhook.last_triggered_at)}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Switch checked={webhook.is_active} onCheckedChange={() => handleToggle(webhook.id, webhook.is_active)} />
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="outline" size="sm" className="h-8 w-8 p-0"
                            onClick={() => handleTest(webhook)}
                            disabled={testing === webhook.id}
                          >
                            {testing === webhook.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <TestTube className="h-4 w-4" />}
                          </Button>
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button variant="destructive" size="sm" className="h-8 w-8 p-0">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Webhook löschen?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  Diese Aktion kann nicht rückgängig gemacht werden.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Abbrechen</AlertDialogCancel>
                                <AlertDialogAction onClick={() => handleDelete(webhook.id)} className="bg-destructive text-destructive-foreground">
                                  Löschen
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Plan Limits Info */}
      <Card>
        <CardHeader>
          <CardTitle className="text-sm">Webhook-Limits pro Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-4 gap-4 text-center text-sm">
            <div><div className="font-semibold">Free</div><div className="text-muted-foreground">0 Webhooks</div></div>
            <div><div className="font-semibold">Basic</div><div className="text-muted-foreground">1 Webhook</div></div>
            <div><div className="font-semibold">Pro</div><div className="text-muted-foreground">5 Webhooks</div></div>
            <div><div className="font-semibold">Premium</div><div className="text-muted-foreground">Unbegrenzt</div></div>
          </div>
        </CardContent>
      </Card>

      {/* Add Webhook Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>Webhook hinzufügen</DialogTitle>
            <DialogDescription>Konfigurieren Sie einen neuen Webhook-Endpunkt für einen Kunden.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Kunde *</Label>
              <Select value={selectedTenant} onValueChange={setSelectedTenant}>
                <SelectTrigger><SelectValue placeholder="Kunde auswählen..." /></SelectTrigger>
                <SelectContent>
                  {tenants.filter(t => t.plan !== 'free').map(t => (
                    <SelectItem key={t.id} value={t.id}>
                      {t.company_name || t.contact_name} ({t.plan}) — {getWebhookCountForTenant(t.id)}/{PLAN_WEBHOOK_LIMITS[t.plan] === 999 ? '∞' : PLAN_WEBHOOK_LIMITS[t.plan]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Event-Typ *</Label>
              <Select value={newEventType} onValueChange={setNewEventType}>
                <SelectTrigger><SelectValue placeholder="Event auswählen..." /></SelectTrigger>
                <SelectContent>
                  {EVENT_TYPES.map(e => (
                    <SelectItem key={e.value} value={e.value}>
                      {e.label} — {e.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Webhook-URL *</Label>
              <Input
                placeholder="https://hooks.zapier.com/hooks/catch/..."
                value={newUrl}
                onChange={(e) => setNewUrl(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Beschreibung</Label>
              <Input
                placeholder="z.B. Zapier → CRM Lead erstellen"
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Shield className="h-4 w-4" /> HMAC Secret Key (optional)
              </Label>
              <Input
                placeholder="Für Payload-Signatur (X-Klartech-Signature Header)"
                value={newSecretKey}
                onChange={(e) => setNewSecretKey(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                Falls gesetzt, wird jeder Payload mit HMAC-SHA256 signiert. Der Empfänger kann die Signatur verifizieren.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>Abbrechen</Button>
            <Button onClick={handleAdd} disabled={saving}>
              {saving ? 'Wird erstellt...' : 'Webhook erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
